import { inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap, of, catchError } from 'rxjs';
import { AvatarService } from '../core/users/avatar';
import { Avatar } from '../core/models/avatar-model';
import { environment } from '../../environments/environment';
import { USERS } from '../../DummyFiles/users';
import { SnackbarService } from './snackbar.service';

/**
 * CurrentUserService
 *
 * Verwaltet den aktuell eingeloggten Benutzer (Entra ID / OID-basiert).
 *
 * Verantwortlichkeiten:
 * - Speichern der OID des eingeloggten Users (gesetzt nach MSAL-Login)
 * - Laden des Avatar-Profils via AvatarService
 * - Bereitstellung eines reaktiven currentUser-Signals
 */
@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private readonly avatarService = inject(AvatarService);
  private readonly snackbarService = inject(SnackbarService);

  /** OID aus dem Entra ID Token – wird nach MSAL-Login gesetzt */
  readonly currentUserOid = signal<string | null>(null);

  /**
   * Setzt die OID des aktuell eingeloggten Users.
   * Wird in app.ts nach erfolgreichem MSAL-Login aufgerufen.
   */
  setCurrentUser(oid: string): void {
    this.currentUserOid.set(oid);
  }

  /**
   * Reaktives Signal mit den vollständigen Avatar-Daten des eingeloggten Users.
   * Ruft nach dem Login POST /user auf (Touch/Create), um den User im Backend zu persistieren.
   * Lädt automatisch neu wenn sich die OID ändert.
   * Bei Fehler: Snackbar-Fehlermeldung, currentUser bleibt null.
   */
  readonly currentUser = toSignal<Avatar | null>(
    toObservable(this.currentUserOid).pipe(
      switchMap(oid =>
        oid
          ? this.avatarService.touchOrCreate().pipe(
              catchError(() => {
                this.snackbarService.error('Benutzer konnte nicht geladen werden');
                return of(null);
              })
            )
          : of(null)
      )
    ),
    { initialValue: null }
  );

  constructor() {
    if (environment.useMocks) {
      this.setCurrentUser(USERS[0].oid);
    }
  }
}
